// @tennis-matches/services/i18n/useI18n
// React hook for i18n that automatically reacts to message updates

import { useMemo, useState, useEffect } from "react";
import { tt, getRemoteMessagesVersion } from "./i18n.js";

/**
 * Custom hook that returns a translation function and automatically
 * re-renders when i18n messages are updated.
 *
 * @param {string} locale - The locale to use for translations
 * @returns {Function} Translation function t(key, vars)
 *
 * @example
 * const t = useI18n(state?.app?.locale || "de-DE");
 * return <h1>{t("title.app")}</h1>;
 */
export function useI18n(locale) {
  // Track i18n messages version to trigger re-render when messages are loaded
  const [i18nVersion, setI18nVersion] = useState(() => {
    try {
      return getRemoteMessagesVersion();
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    const handleI18nUpdate = () => {
      try {
        const newVersion = getRemoteMessagesVersion();
        if (newVersion !== i18nVersion) {
          setI18nVersion(newVersion);
        }
      } catch (e) {
        console.warn("[useI18n] Error getting messages version:", e);
      }
    };

    window.addEventListener("i18n-messages-updated", handleI18nUpdate);
    return () => {
      window.removeEventListener("i18n-messages-updated", handleI18nUpdate);
    };
  }, [i18nVersion]);

  // Recreate tt function when locale or i18nVersion changes
  // This ensures the translator function has access to the latest REMOTE_MESSAGES
  const t = useMemo(() => {
    const loc = locale || "de-DE";
    return tt(loc);
  }, [locale, i18nVersion]);

  return t;
}
